/**
 * Advertiser self-serve portal read contracts (WEB-587).
 *
 * The wizard's venue-claim and event steps need two reads that no existing
 * advertiser-callable route provided:
 *
 *   - **claimable venues** — `POST /me/venue-claims` takes a `locationId`, but
 *     the public `GET /api/v1/venues` projection deliberately exposes only a
 *     `slug` (no id, address, or coordinates). A claim step built on it would
 *     have nothing to submit.
 *   - **sponsorable events** — `POST /me/sponsored-listings` takes an `eventId`
 *     and enforces a PREMIUM quality bar, but `GET /api/v1/venues/:slug/shows`
 *     returns `eventSlug` with no id and no quality verdict, and is keyed by
 *     slug while a claim carries a `locationId`. The only projection with both
 *     is `GET /api/v1/admin/events`, which is ADMIN-gated.
 *
 * Both are scoped to the caller's advertiser via `req.user.id →
 * User.advertiserId`; neither takes an advertiser argument, so there is no
 * cross-advertiser IDOR to get wrong (same contract as the sibling `/me/*`
 * routes).
 */
import { z } from 'zod';

import { venueCityCodeSchema } from './venues';

/** Default/maximum rows returned by either portal read. */
export const ADVERTISER_PORTAL_DEFAULT_LIMIT = 25;
export const ADVERTISER_PORTAL_MAX_LIMIT = 50;

const portalLimitSchema = z.coerce
  .number()
  .int()
  .min(1, 'limit must be >= 1')
  .max(ADVERTISER_PORTAL_MAX_LIMIT, `limit must be <= ${ADVERTISER_PORTAL_MAX_LIMIT}`)
  .default(ADVERTISER_PORTAL_DEFAULT_LIMIT);

// ---------------------------------------------------------------------------
// Claimable venues
// ---------------------------------------------------------------------------

export const claimableVenuesQuerySchema = z
  .object({
    /**
     * Free-text venue-name search. Absent returns the city's venues in name
     * order, which is what the step renders before the advertiser types.
     */
    q: z.string().trim().min(1).max(120).optional(),
    cityCode: venueCityCodeSchema.default('CLT'),
    limit: portalLimitSchema,
  })
  .strict();
export type ClaimableVenuesQuery = z.infer<typeof claimableVenuesQuerySchema>;

/**
 * A venue the advertiser can submit a claim for.
 *
 * `locationId` is deliberately named for its use — it is the exact value
 * `createVenueClaimSchema` expects, so the step never has to translate a slug.
 * Coordinates are nullable because `Location.coordinates` is nullable; the row
 * renders without its coord line rather than inventing one.
 *
 * The search does NOT pre-filter venues another advertiser already holds.
 * Exclusivity is enforced at submit time as `409 VENUE_ALREADY_CLAIMED`, and
 * DES-56 designs that as a distinct explanatory screen — hiding the venue here
 * would replace an explanation with an unexplained absence.
 */
export const claimableVenuePayloadSchema = z
  .object({
    locationId: z.string(),
    name: z.string(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    neighborhoodName: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
  })
  .strict();
export type ClaimableVenuePayload = z.infer<typeof claimableVenuePayloadSchema>;

// ---------------------------------------------------------------------------
// Sponsorable events
// ---------------------------------------------------------------------------

export const sponsorableEventsQuerySchema = z
  .object({
    /** Narrow to one approved venue. Omitted → events across every approved claim. */
    locationId: z.string().trim().min(1).max(128).optional(),
    limit: portalLimitSchema,
  })
  .strict();
export type SponsorableEventsQuery = z.infer<typeof sponsorableEventsQuerySchema>;

/**
 * An upcoming event at a venue the advertiser holds an APPROVED claim on.
 *
 * Ineligible rows are returned rather than filtered out: DES-56 renders them
 * with a `NOT ELIGIBLE` badge and the reason inline, so an advertiser can see
 * why an event they know about cannot be sponsored. `eligible` mirrors the
 * create route's gate exactly (`qualityStatus === 'PREMIUM'`), so the picker
 * cannot drift from what `POST /me/sponsored-listings` will accept.
 */
export const sponsorableEventPayloadSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    date: z.string().datetime(),
    locationId: z.string(),
    locationName: z.string().nullable(),
    categoryName: z.string().nullable(),
    qualityScore: z.number().nullable(),
    qualityStatus: z.string().nullable(),
    qualityReasons: z.array(z.string()),
    eligible: z.boolean(),
  })
  .strict();
export type SponsorableEventPayload = z.infer<typeof sponsorableEventPayloadSchema>;
